import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { RefreshCw, AlertCircle, CheckCircle } from 'lucide-react'
import { authApi } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

type Status = 'loading' | 'success' | 'error'

export default function AuthCallbackPage() {
  const { provider } = useParams<{ provider: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { login } = useAuthStore()

  const [status, setStatus] = useState<Status>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code')
      const error = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      // Check for OAuth errors
      if (error) {
        setStatus('error')
        setErrorMessage(errorDescription || error || '인증에 실패했습니다')
        return
      }

      if (!code) {
        setStatus('error')
        setErrorMessage('인증 코드가 없습니다')
        return
      }

      if (!provider || !['google', 'kakao'].includes(provider)) {
        setStatus('error')
        setErrorMessage('지원하지 않는 로그인 방식입니다')
        return
      }

      try {
        // Get redirect URI (same as login page)
        const redirectUri = `${window.location.origin}/auth/${provider}/callback`

        // Call API to exchange code for token
        let result
        if (provider === 'google') {
          result = await authApi.loginGoogle(code, redirectUri)
        } else {
          result = await authApi.loginKakao(code, redirectUri)
        }

        if (!result.success || !result.data) {
          setStatus('error')
          setErrorMessage(result.error?.message || '로그인에 실패했습니다')
          return
        }

        // Save auth data to store (token is now in HttpOnly cookie)
        const { user } = result.data
        login(
          {
            id: user.id,
            email: user.email,
            name: user.name,
            avatarUrl: user.avatarUrl,
            tier: user.tier as 'free' | 'basic' | 'premium' | 'enterprise',
          },
          user.points
        )

        setStatus('success')

        // Redirect to home after short delay
        setTimeout(() => {
          const returnUrl = sessionStorage.getItem('auth_return_url') || '/'
          sessionStorage.removeItem('auth_return_url')
          navigate(returnUrl, { replace: true })
        }, 1500)
      } catch (err) {
        setStatus('error')
        setErrorMessage(err instanceof Error ? err.message : '로그인 처리 중 오류가 발생했습니다')
      }
    }

    handleCallback()
  }, [provider, searchParams, login, navigate])

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="card p-8 text-center">
          {status === 'loading' && (
            <>
              <RefreshCw className="w-12 h-12 text-accent-gold mx-auto animate-spin mb-4" />
              <h1 className="text-xl font-bold mb-2">로그인 처리 중</h1>
              <p className="text-text-secondary">잠시만 기다려주세요...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 text-accent-success mx-auto mb-4" />
              <h1 className="text-xl font-bold mb-2">로그인 성공!</h1>
              <p className="text-text-secondary">잠시 후 이동합니다...</p>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-12 h-12 text-accent-error mx-auto mb-4" />
              <h1 className="text-xl font-bold mb-2">로그인 실패</h1>
              <p className="text-text-secondary mb-4">{errorMessage}</p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="btn btn-primary"
              >
                다시 시도하기
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
